import { useState } from 'react';
import { useQuery, useMutation } from 'react-query';
import { REQUEST_ACTIONS } from '~/Constants/Actions.constants';
import { CORE_SERVICE_URLS } from '~/Constants/Urls.constants';
import { requestReactQuery } from '~/Helpers/ReactQuery.helper';
import { routeReplacer } from '~/Helpers/String.helper';
import { useProvider } from '~/Hooks';
import { IErrorResponse } from '~/Models/DTO/Error.models';
import { TIME_VALUES } from '~/Constants/Date.constants';

export type EverSportExportResponse = {
  data?: any;
  status?: string;
  failureMessage?: string;
  link?: string;
};

export const useGetAuthLink = () => {
  const { currentProvider } = useProvider();
  const response = useQuery<EverSportExportResponse, IErrorResponse>(
    [REQUEST_ACTIONS.GET_EVERSPORTS_AUTH_LINK, currentProvider?.domain],
    () =>
      requestReactQuery<EverSportExportResponse>({
        url: routeReplacer(CORE_SERVICE_URLS.GET_EVERSPORTS_AUTH_LINK, {
          domain: currentProvider?.domain || '',
        }),
        config: { method: 'GET' },
      }),
    {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
      enabled: false,
    },
  );
  return response;
};
export const useTriggerGetStatus = (uuid = '') => {
  const [status, setStatus] = useState<EverSportExportResponse | null>(null);
  const { refetch } = useQuery<EverSportExportResponse, IErrorResponse>(
    [REQUEST_ACTIONS.GET_EXPORT_STATUS, uuid],
    async () => {
      const response = await requestReactQuery<EverSportExportResponse>({
        url: routeReplacer(CORE_SERVICE_URLS.GET_EXPORT_STATUS, { uuid }),
        config: { method: 'GET' },
      });
      setStatus(response);
      if (response?.status !== 'Success') {
        throw response;
      }
      return response;
    },
    {
      retry: (failureCount: number, error: any) => {
        // Retry if the status is not 'Success' and the error is not a failure message
        if (!['InProgress', 'Pending'].includes(error?.status)) {
          return false;
        }
        return failureCount < 50; // Retry up to 10 times
      },
      retryDelay: 5000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
      refetchInterval: TIME_VALUES.HOUR,
      enabled: !!uuid?.length, // Disabled by default; we'll trigger refetch with channelId manually
    },
  );
  return {
    refetch: (uuid: string) => refetch({ queryKey: uuid }),
    data: status,
  };
};

export const useTriggerExport = () => {
  const { currentProvider } = useProvider();
  const mutation = useMutation<any, IErrorResponse, any>(
    [REQUEST_ACTIONS.START_EXPORT, currentProvider?.domain],
    (params: any) =>
      requestReactQuery<EverSportExportResponse>({
        url: routeReplacer(CORE_SERVICE_URLS.START_EXPORT, {
          domain: currentProvider?.domain || '',
        }),
        config: { method: 'POST' },
        data: params,
      }),
    {
      retry: false,
    },
  );
  return {
    ...mutation,
  };
};
